import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { usePageMotion } from "../hooks/usePageMotion";
import { ProfileAvatar } from "../pages/ProfilesPage";

function NavIcon({ name }) {
  const props = {
    className: "nav-icon",
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (name) {
    case "home":
      return (
        <svg {...props}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5.5 9.5V21h13V9.5" />
          <path d="M9.5 21v-7h5v7" />
        </svg>
      );
    case "movie":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M7 5v14M17 5v14M3 9h4M3 15h4M17 9h4M17 15h4" />
        </svg>
      );
    case "series":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="12" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      );
    case "anime":
      return (
        <svg {...props}>
          <path d="M12 3l1.6 4.8L18.5 9.5l-4.9 1.7L12 16l-1.6-4.8L5.5 9.5l4.9-1.7L12 3z" />
          <path d="M18.5 15.5 19.2 17.7 21.5 18.5l-2.3.8-.7 2.2-.7-2.2-2.3-.8 2.3-.8.7-2.2z" />
        </svg>
      );
    case "admin":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 3v2.2M12 18.8V21M4.9 6.3l1.6 1.6M17.5 16.1l1.6 1.6M3 12h2.2M18.8 12H21M4.9 17.7l1.6-1.6M17.5 7.9l1.6-1.6" />
        </svg>
      );
    case "users":
      return (
        <svg {...props}>
          <circle cx="9" cy="8" r="3" />
          <path d="M3.5 19c.6-3 2.7-4.5 5.5-4.5S13.9 16 14.5 19" />
          <circle cx="17" cy="9" r="2.4" />
          <path d="M15.2 14.6c2 .4 3.5 1.7 4.3 4.4" />
        </svg>
      );
    case "settings":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9c.3.6.9 1 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      );
    case "favorites":
      return (
        <svg {...props}>
          <path d="M12 3.6 14.4 9l5.8.5-4.4 3.8 1.4 5.6L12 16.2 6.8 18.9l1.4-5.6L3.8 9.5 9.6 9 12 3.6z" />
        </svg>
      );
    case "live":
      return (
        <svg {...props}>
          <rect x="3" y="6" width="18" height="13" rx="2" />
          <path d="M8 3 12 6 16 3" />
          <circle cx="12" cy="12.5" r="2.5" fill="currentColor" stroke="none" />
        </svg>
      );
    case "sagas":
      return (
        <svg {...props}>
          <rect x="4" y="5" width="11" height="14" rx="1.5" />
          <path d="M15 7h3.5a1.5 1.5 0 0 1 1.5 1.5v11a1.5 1.5 0 0 1-1.5 1.5H9" />
          <path d="M7 9h5M7 12h5M7 15h3" />
        </svg>
      );
    case "remote":
      return (
        <svg {...props}>
          <rect x="5" y="2" width="14" height="20" rx="2" />
          <circle cx="12" cy="18" r="1" fill="currentColor" stroke="none" />
          <path d="M9 6h6M9 9.5h6M9 13h4" />
        </svg>
      );
    default:
      return null;
  }
}

function NavItem({ to, end, icon, children, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} className="nav-link">
      <NavIcon name={icon} />
      <span>{children}</span>
    </NavLink>
  );
}

export default function Shell({ children, wide = false, iptvLayout = false }) {
  const { user, profile, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("q") || "";
  });

  usePageMotion(mainRef);

  useEffect(() => {
    if (!iptvLayout) return undefined;
    document.documentElement.classList.add("iptv-viewport-lock");
    document.body.classList.add("iptv-viewport-lock");
    return () => {
      document.documentElement.classList.remove("iptv-viewport-lock");
      document.body.classList.remove("iptv-viewport-lock");
    };
  }, [iptvLayout]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearch(params.get("q") || "");
  }, [location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setProfileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.body.classList.add("nav-open");
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("nav-open");
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    function onDoc(e) {
      if (!profileMenuRef.current?.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") setProfileMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [profileMenuOpen]);

  function searchBasePath(pathname) {
    if (pathname.startsWith("/buscar")) return "/buscar";
    if (pathname.startsWith("/peliculas")) return "/peliculas";
    if (pathname.startsWith("/anime")) return "/anime";
    if (pathname.startsWith("/series")) return "/series";
    if (pathname.startsWith("/sagas")) return "/sagas";
    if (pathname.startsWith("/iptv")) return "/iptv";
    if (pathname.startsWith("/favoritos")) return "/favoritos";
    if (pathname.startsWith("/watch")) return "/";
    return pathname === "/" ? "/" : pathname || "/";
  }

  function applySearch(value) {
    setSearch(value);
    const trimmed = value.trim();
    if (trimmed.length >= 2) {
      if (location.pathname.startsWith("/iptv")) {
        navigate(
          { pathname: "/iptv", search: `?q=${encodeURIComponent(trimmed)}` },
          { replace: true }
        );
      } else {
        navigate(
          { pathname: "/buscar", search: `?q=${encodeURIComponent(trimmed)}` },
          { replace: true }
        );
      }
      return;
    }
    if (location.pathname === "/buscar") {
      navigate("/", { replace: true });
      return;
    }
    if (location.pathname.startsWith("/iptv")) {
      const p = new URLSearchParams(location.search);
      p.delete("q");
      const qs = p.toString();
      navigate({ pathname: "/iptv", search: qs ? `?${qs}` : "" }, { replace: true });
      return;
    }
    const base = searchBasePath(location.pathname);
    navigate({ pathname: base, search: "" }, { replace: true });
  }

  return (
    <div className={`app-shell ${wide ? "app-shell-wide" : ""}${iptvLayout ? " app-shell-iptv" : ""}`}>
      <header className={`topbar ${scrolled || menuOpen ? "topbar-solid" : ""}`}>
        <div className="topbar-main">
          <Link to="/" className="topbar-brand" onClick={() => setMenuOpen(false)}>
            Casasm
          </Link>

          <nav className="topbar-nav-desktop" aria-label="Principal">
            <NavItem to="/" end icon="home">
              Inicio
            </NavItem>
            <NavItem to="/iptv" icon="live">
              IPTV
            </NavItem>
            <NavItem to="/peliculas" icon="movie">
              Películas
            </NavItem>
            <NavItem to="/series" icon="series">
              Series
            </NavItem>
            <NavItem to="/anime" icon="anime">
              Anime
            </NavItem>
            <NavItem to="/favoritos" icon="favorites">
              Favoritos
            </NavItem>
            <NavItem to="/mando" icon="remote">
              Mando TV
            </NavItem>
          </nav>

          <div className="topbar-user topbar-user-desktop">
            <label className="topbar-search">
              <span className="sr-only">Buscar</span>
              <input
                type="search"
                placeholder="Películas, series o canales…"
                value={search}
                onChange={(e) => applySearch(e.target.value)}
              />
            </label>
            {profile ? (
              <div className="topbar-profile-wrap" ref={profileMenuRef}>
                <button
                  type="button"
                  className="topbar-profile"
                  aria-haspopup="menu"
                  aria-expanded={profileMenuOpen}
                  onClick={() => setProfileMenuOpen((v) => !v)}
                >
                  <ProfileAvatar profile={profile} size={32} className="topbar-avatar" />
                  <span className="topbar-profile-name">{profile.name}</span>
                </button>
                {profileMenuOpen ? (
                  <div className="topbar-profile-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate("/profiles");
                      }}
                    >
                      Cambiar de usuario
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate("/settings");
                      }}
                    >
                      Mis configuraciones
                    </button>
                    {isAdmin ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          navigate("/admin");
                        }}
                      >
                        Administración
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <span className="topbar-email" title={user?.email}>
                {user?.name || user?.email}
              </span>
            )}
          </div>

          <button
            type="button"
            className={`nav-toggle ${menuOpen ? "open" : ""}`}
            aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <div
          id="mobile-nav"
          className={`topbar-drawer ${menuOpen ? "open" : ""}`}
        >
          <label className="topbar-search topbar-search-mobile">
            <span className="sr-only">Buscar</span>
            <input
              type="search"
              placeholder="Películas, series o canales…"
              value={search}
              onChange={(e) => applySearch(e.target.value)}
            />
          </label>
          <nav className="topbar-nav-mobile" aria-label="Menú móvil">
            <NavItem to="/" end icon="home" onClick={() => setMenuOpen(false)}>
              Inicio
            </NavItem>
            <NavItem to="/iptv" icon="live" onClick={() => setMenuOpen(false)}>
              IPTV
            </NavItem>
            <NavItem to="/peliculas" icon="movie" onClick={() => setMenuOpen(false)}>
              Películas
            </NavItem>
            <NavItem to="/series" icon="series" onClick={() => setMenuOpen(false)}>
              Series
            </NavItem>
            <NavItem to="/anime" icon="anime" onClick={() => setMenuOpen(false)}>
              Anime
            </NavItem>
            <NavItem to="/favoritos" icon="favorites" onClick={() => setMenuOpen(false)}>
              Favoritos
            </NavItem>
            <NavItem to="/mando" icon="remote" onClick={() => setMenuOpen(false)}>
              Mando TV
            </NavItem>
            <NavItem to="/profiles" icon="users" onClick={() => setMenuOpen(false)}>
              Cambiar de usuario
            </NavItem>
            <NavItem to="/settings" icon="settings" onClick={() => setMenuOpen(false)}>
              Mis configuraciones
            </NavItem>
            {isAdmin ? (
              <NavItem to="/admin" icon="admin" onClick={() => setMenuOpen(false)}>
                Administración
              </NavItem>
            ) : null}
          </nav>
          <div className="topbar-user topbar-user-mobile">
            {profile ? (
              <span className="topbar-profile topbar-profile-static">
                <ProfileAvatar profile={profile} size={32} className="topbar-avatar" />
                <span className="topbar-profile-name">{profile.name}</span>
              </span>
            ) : (
              <span className="topbar-email">{user?.name || user?.email}</span>
            )}
          </div>
        </div>
      </header>

      {menuOpen ? (
        <button
          type="button"
          className="nav-backdrop"
          aria-label="Cerrar menú"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <main
        ref={mainRef}
        className={`app-main page-motion-root${iptvLayout ? " app-main-iptv" : ""}`}
      >
        {children}
      </main>
    </div>
  );
}
